import { httpsCallable, FunctionsError } from 'firebase/functions';
import { functions } from '../firebase';
import type { Reaction } from '../../shared/schemas';

/**
 * Typed wrappers around the Cloud Functions callables.
 *
 * Every write to `puns` goes through here. `firestore.rules` denies the client
 * writing them directly, so this is the only route — which is what makes the
 * game's rules (one pun per day, one crown per day, no self-reactions)
 * enforceable rather than advisory.
 */

interface SubmitPunResult {
  punId: string;
  pstDay: string;
}

/**
 * Turn a callable failure into something worth showing a player.
 *
 * The server sends a specific `code` for each rule it enforces; anything
 * unrecognised falls back to a generic message rather than leaking internals.
 */
function messageFor(error: unknown, fallback: string): string {
  if (!(error instanceof FunctionsError)) return fallback;

  switch (error.code) {
    case 'functions/unauthenticated':
      return 'You need to be signed in to do that.';
    case 'functions/already-exists':
      return "You've already submitted your pun today. Come back tomorrow!";
    case 'functions/resource-exhausted':
      return 'You can only give one crown per day. Choose wisely!';
    case 'functions/failed-precondition':
      // Covers both "no prompt yet" and "cannot react to your own pun"; the
      // server message is player-facing in both cases.
      return error.message || fallback;
    case 'functions/not-found':
      return 'That pun no longer exists.';
    case 'functions/invalid-argument':
      return error.message || fallback;
    default:
      return fallback;
  }
}

/** Thrown with a message that is safe to render directly. */
export class ApiError extends Error {
  /** The Functions error code, e.g. `functions/already-exists`, when there was one. */
  readonly code: string;

  constructor(message: string, code = 'unknown') {
    super(message);
    this.name = 'ApiError';
    this.code = code;
  }
}

const codeOf = (error: unknown): string =>
  error instanceof FunctionsError ? error.code : 'unknown';

/**
 * Submit today's pun. The server decides the day, the prompt and the timestamp;
 * only the text is sent.
 */
export async function submitPun(text: string): Promise<SubmitPunResult> {
  try {
    const call = httpsCallable<{ text: string }, SubmitPunResult>(functions, 'submitPun');
    return (await call({ text })).data;
  } catch (error) {
    throw new ApiError(
      messageFor(error, 'Unable to submit your pun. Please try again.'),
      codeOf(error),
    );
  }
}

/**
 * Set, change or clear the caller's reaction to a pun.
 * Passing the reaction already selected clears it; `null` clears it explicitly.
 */
export async function setReaction(punId: string, reaction: Reaction | null): Promise<void> {
  try {
    const call = httpsCallable<{ punId: string; reaction: Reaction | null }, { ok: boolean }>(
      functions,
      'setReaction',
    );
    await call({ punId, reaction });
  } catch (error) {
    throw new ApiError(
      messageFor(error, 'Unable to save your reaction. Please try again.'),
      codeOf(error),
    );
  }
}
